import { type ReactNode } from 'react';
import { ArrowRight as ArrowRightIcon } from '@phosphor-icons/react';
import { InternalUiRenderer } from '@erp/json-renderer/react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { PortalBrandTheme } from '@/pages/portal/brandTheme';
import { PortalWordmark } from '@/pages/portal/route';
import { PortalLoginPanel } from '@/pages/portal/PortalLoginPanel';
import {
  cannedBrandTokens,
  cannedCompanyName,
  cannedPortalSpec,
  cannedPortalState,
} from '@/pages/portal/cannedPortal';
import { setPortalLoggedIn } from '@/pages/portal/portal-session';

interface PortalGeneratedCardProps {
  /** The `generate_portal` tool-call args: `{ companyName? }`. */
  args: Record<string, unknown>;
  className?: string;
}

/** Brand roles surfaced as little swatches, in display order. */
const SWATCH_ROLES = [
  'primary',
  'secondary',
  'accent',
  'success',
  'danger',
  'border',
] as const;

/**
 * A non-interactive, CSS-scaled thumbnail of a full portal screen. Renders its
 * children at a larger logical width, then scales the whole thing down so it
 * reads as a faithful preview inside the chat. `pointer-events-none` keeps it
 * look-only.
 */
function PreviewFrame({
  height,
  scale,
  children,
}: {
  height: number;
  scale: number;
  children: ReactNode;
}) {
  return (
    <div
      className="relative overflow-hidden rounded-md border border-border"
      style={{ height }}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute left-0 top-0 origin-top-left"
        style={{ width: `${100 / scale}%`, transform: `scale(${scale})` }}
      >
        {children}
      </div>
    </div>
  );
}

function PreviewLabel({ children }: { children: ReactNode }) {
  return (
    <div className="mb-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
      {children}
    </div>
  );
}

/**
 * Result card for the `generate_portal` tool — a (canned) "your branded portal
 * is ready" card rendered inline in the chat. Shows side-by-side previews of
 * the login screen and the dashboard (with canned data), plus an Open button
 * that drops the user into the portal. Mirrors `ChartToolResult` /
 * `InternalUiToolResult`: rendered from the tool-call args so it works in every
 * chat surface.
 */
export function PortalGeneratedCard({
  args,
  className,
}: PortalGeneratedCardProps) {
  const company =
    typeof args.companyName === 'string' && args.companyName.trim()
      ? args.companyName.trim()
      : cannedCompanyName;

  const swatches = (cannedBrandTokens.colors ?? []).filter((color) =>
    SWATCH_ROLES.includes(color.role as (typeof SWATCH_ROLES)[number]),
  );

  function openPortal() {
    // Open the branded login page in a NEW TAB — not the already-logged-in
    // dashboard. Clear any prior portal session so `/portal/login` doesn't
    // redirect to `/portal`.
    setPortalLoggedIn(false);
    window.open('/portal/login', '_blank', 'noopener,noreferrer');
  }

  return (
    <PortalBrandTheme
      tokens={cannedBrandTokens}
      className={cn('rounded-lg border border-border p-4', className)}
    >
      <div className="flex items-center justify-between gap-2">
        <PortalWordmark className="text-lg font-semibold tracking-tight" />
        <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          Portal generated
        </span>
      </div>

      <p className="mt-1.5 text-sm text-muted-foreground">
        Your branded customer portal for {company} is ready — here's a preview
        of the sign-in screen and the dashboard.
      </p>

      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <PreviewLabel>Login</PreviewLabel>
          <PreviewFrame height={260} scale={0.4}>
            <div className="flex min-h-[650px] items-center justify-center bg-[var(--background)] p-6">
              <PortalLoginPanel email="" password="" disabled />
            </div>
          </PreviewFrame>
        </div>
        <div>
          <PreviewLabel>Dashboard</PreviewLabel>
          <PreviewFrame height={260} scale={0.4}>
            <div className="bg-[var(--background)]">
              <InternalUiRenderer
                spec={cannedPortalSpec}
                state={cannedPortalState}
              />
            </div>
          </PreviewFrame>
        </div>
      </div>

      <div className="mt-3 flex items-center gap-1.5">
        {swatches.map((color) => (
          <span
            key={color.role}
            title={color.label ?? color.role}
            className="size-5 rounded-full border border-black/5"
            style={{ backgroundColor: color.value }}
          />
        ))}
      </div>

      <Button className="mt-4 gap-1.5" onClick={openPortal}>
        Open {company} portal
        <ArrowRightIcon className="size-4" weight="bold" />
      </Button>
    </PortalBrandTheme>
  );
}
